import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  useVirtualGiftsIsOpen,
  virtualGiftsCloseModal,
  useVirtualGiftsData,
  SendMessage,
  messageTypes,
  VirtualGiftType,
  useVirtualGiftsSelectVirtualGift,
  virtualGiftsSelectVirtualGift,
  GetVirtualGifts
} from 'entities/messages'
import {useWallet} from 'entities/payment/model'
import {useScroll} from 'shared/hooks/useScroll'
import {mediaVariants} from 'shared/lib'
import {Badge} from 'shared/ui/badge'
import {Button} from 'shared/ui/buttons'
import {Cross} from 'shared/ui/cross'
import {ArrowLeft, GoCoin20} from 'shared/ui/icons'
import {Modal} from 'shared/ui/modal'
import {TextField} from 'shared/ui/textField'
import styles from './styles.module.scss'

type VirtualGiftsModalProps = {
  idUser: string
  onSendHandler?: () => void
}

export const VirtualGiftsModal: FC<VirtualGiftsModalProps> = ({idUser, onSendHandler}) => {
  const dispatch = useDispatch()
  const isOpen = useVirtualGiftsIsOpen()
  const selectedVirtualGift = useVirtualGiftsSelectVirtualGift()
  const virtualGiftCategories = useVirtualGiftsData()
  const {refetch} = GetVirtualGifts(dispatch)
  const {virtualGifts} = useWallet()

  useEffect(() => {
    isOpen && refetch().catch(console.error)
  }, [isOpen])

  const onClose = useCallback(() => dispatch(virtualGiftsCloseModal()), [dispatch])

  const clickVirtualGiftHandler = useCallback((virtualGift: VirtualGiftType) => {
    dispatch(virtualGiftsSelectVirtualGift(virtualGift))
  }, [dispatch, virtualGiftsSelectVirtualGift])

  if (!isOpen || !idUser) return <></>

  return (
    <Modal open={isOpen} onClose={onClose}>
      <>
        {/* Made via active to save scroll on return */}
        <div
          className={`${styles.allVirtualGifts} ${!selectedVirtualGift ? styles.active : ''}`}>
          <div className={styles.header}>
            Select a gift<Cross onClose={onClose} />
          </div>

          <div className={styles.customScroll}>
            {
              virtualGiftCategories.map((category) => <div
                key={category.name}
                className={styles.virtualGiftsWrapper}
              >
                <div className={styles.virtualGiftsHeader}>
                  {category.name}
                  {category.virtualGifts[0]?.price === 0 ? <Badge>x{virtualGifts}</Badge> : null}
                </div>
                <div className={styles.virtualGifts}>
                  {category.virtualGifts.map((virtualGift) =>
                    <VirtualGift key={virtualGift.id} virtualGift={virtualGift} onClick={clickVirtualGiftHandler} />)}
                </div>
              </div>)
            }
          </div>
        </div>
        {selectedVirtualGift ? <SendVirtualGifts idUser={idUser} onSendHandler={onSendHandler} /> : null}
      </>
    </Modal>
  )
}

type VirtualGiftProps = {
  virtualGift: VirtualGiftType
  onClick: (virtualGift: VirtualGiftType) => void
}

const VirtualGift: FC<VirtualGiftProps> = ({virtualGift, onClick}) => {
  return (
    <div className={styles.virtualGift} onClick={() => onClick(virtualGift)}>
      <div className={styles.preview}>
        <img src={virtualGift.data.url + mediaVariants.square256} className={styles.img} alt='virtualGift' />
      </div>
      <div className={styles.name}>{virtualGift.name}</div>
      <div className={styles.price}>
        <span>{virtualGift.price}</span>
        <GoCoin20 />
      </div>
    </div>
  )
}

type SendVirtualGiftsProps = {
  idUser: string
  onSendHandler?: () => void
}

const SendVirtualGifts: FC<SendVirtualGiftsProps> = ({idUser, onSendHandler}) => {
  const dispatch = useDispatch()
  const [selectedBackgroundId, setSelectedBackgroundId] = useState(1)
  const [message, setMessage] = useState('')
  const selectedVirtualGift = useVirtualGiftsSelectVirtualGift() as VirtualGiftType
  const ref = useRef<HTMLDivElement | null>(null)
  const scroll = useScroll(ref)
  const [isBottomBorder, setIsBottomBorder] = useState(false)
  const {refetch, isFetching} = SendMessage(
    idUser,
    {id: selectedVirtualGift.id, text: message || null, gradient: selectedBackgroundId.toString()},
    messageTypes.virtualGift,
    dispatch
  )

  useEffect(() => {
    if (!ref.current) return

    const scrollHeight = ref.current.scrollHeight
    const clientHeight = ref.current.clientHeight

    setIsBottomBorder(scroll + clientHeight !== scrollHeight)
  }, [scroll, ref.current?.clientHeight])

  const onBackHandler = useCallback(() => {
    dispatch(virtualGiftsSelectVirtualGift(undefined))
  }, [dispatch])

  const onClose = useCallback(() => dispatch(virtualGiftsCloseModal()), [dispatch, virtualGiftsCloseModal])

  const sendVirtualGift = async () => {
    if (isFetching) return

    refetch()
      .then(({status, error}) => {
        if (status === 'error') throw error

        onSendHandler && onSendHandler()
        onClose()
      })
      .catch(console.error)
  }

  return (
    <div className={`${styles.sendVirtualGifts} ${selectedVirtualGift ? styles.active : ''}`}>
      <div className={styles.header}>
        <div className={styles.headerLeft} onClick={onBackHandler}>
          <div className={styles.arrowLeft}><ArrowLeft /> </div>
          Gifts
        </div>
        <Cross onClose={onClose} />
      </div>
      <div
        className={`
          ${styles.selectedVirtualGiftWrapper}
          ${styles.customScroll}
          ${isBottomBorder ? styles.borderBottom : null}
        `}
        ref={ref}
      >
        <div className={`${styles.selectedVirtualGift} ${styles[`backgroundGradient${selectedBackgroundId}`]}`}>
          <img src={selectedVirtualGift.data.url + mediaVariants.square512} alt='' className={styles.img} />
        </div>

        <div className={styles.summary}>
          <div>{selectedVirtualGift.name}</div>
          <div className={styles.price}>{selectedVirtualGift.price} <GoCoin20 /></div>
        </div>

        <div className={styles.changeBackgroundWrapper}>
          {Array.from({length: 6}, (_, index) =>
            <div
              key={index}
              className={`${styles.item} ${selectedBackgroundId === index + 1 ? styles.active : ''}`}
              onClick={() => setSelectedBackgroundId(index + 1)}
            >
              <div className={`${styles.insideItem} ${styles[`backgroundGradient${index + 1}`]}`}></div>
            </div>
          )}
        </div>

        <TextField
          label='Message'
          placeholder='Enter'
          multiline
          inputContainerStyles={{height: 130}}
          withCounter
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
      </div>
      <div className={styles.buttons} onClick={sendVirtualGift}>
        <Button styleType='primary' fullwidth isLoading={isFetching}>Send</Button>
      </div>
    </div>
  )
}